import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { DataTableCustomized } from '@/components/ui/data-table-customized'
import { FileSpreadsheet } from 'lucide-react'
import { Button } from 'vnetwork-ui'
import type { RevenueOverviewParams } from '../../types'
import type { ProductGroupMonthlyRevenue } from '../../types'
import { useOverviewData } from './overview-table/use-overview-data'
import { OverviewTableLegend } from './overview-table/overview-table-legend'
import {
    buildOverviewColumns,
    getOverviewFooter,
} from './overview-table/overview-columns'
import { OverviewTableHeader } from './overview-table/overview-table-header'
import { CompareAccountingDialog } from './overview-table/compare-accounting-dialog'

interface OverviewTableProps {
    filters: RevenueOverviewParams
}

const OverviewTable: React.FC<OverviewTableProps> = ({ filters }) => {
    const { t } = useTranslation('report')
    const [mergeAllocated, setMergeAllocated] = useState(false)
    const [showCompareDialog, setShowCompareDialog] = useState(false)
    const { productGroups, months, grandTotal, columnTotals, isLoading } =
        useOverviewData(filters)

    const columns = useMemo(
        () => buildOverviewColumns(months, mergeAllocated, grandTotal, t),
        [months, mergeAllocated, grandTotal, t]
    )

    const footerRow = useMemo(
        () =>
            getOverviewFooter(
                columns,
                months,
                mergeAllocated,
                columnTotals,
                grandTotal,
                t
            ),
        [columns, months, mergeAllocated, columnTotals, grandTotal, t]
    )

    return (
        <div className="space-y-2">
            <div className="flex items-center justify-between gap-2">
                <OverviewTableLegend
                    mergeAllocated={mergeAllocated}
                    onMergeChange={setMergeAllocated}
                />
                <Button
                    variant="outlined"
                    size="small"
                    className="rounded-sm shrink-0"
                    onClick={() => setShowCompareDialog(true)}
                >
                    <FileSpreadsheet className="h-4 w-4 mr-1.5" />
                    {t('compare_accounting.title')}
                </Button>
            </div>

            <DataTableCustomized<ProductGroupMonthlyRevenue>
                columns={columns}
                rows={productGroups}
                loading={isLoading}
                showIndex
                bordered
                footerRow={footerRow}
                maxHeight="calc(100vh - 380px)"
                pinnedHeader
                customHeader={
                    <OverviewTableHeader
                        months={months}
                        mergeAllocated={mergeAllocated}
                    />
                }
                rowKey={(row) => row.product_group_name}
                emptyMessage={t('no_data')}
                stickyHeader={{ enabled: false }}
            />

            <CompareAccountingDialog
                open={showCompareDialog}
                onOpenChange={setShowCompareDialog}
                defaultMonths={months}
            />
        </div>
    )
}

export default OverviewTable
